<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import JobadDateModal from './CompanyProfileJobadDateModal.vue';
import DsIconButton from 'JIX/components/ds/DsIconButton.vue';
import { getDateFormatter } from 'JIX/utils/date.js';
import T from './CompanyProfileJobadStatisticsTab.i18n.js';
import 'icons/button-edit.svg';

import { getStandardSplineChartConfig } from 'JIX/highcharts/configs/spline_chart.js';
import { getStandardBarChartConfig } from 'JIX/highcharts/configs/bar_chart.js';

const store = useStore();

const formatDate = getDateFormatter();

let c1 = '#2f786c';
let c2 = '#808080';

const jobaddatemodalchannels = ref();
const jobaddatemodalareas = ref();
const adstatisticsContent = computed(() => store.state.adstatisticsContent);
const companyName = computed(() => store.state.companyName);

const startDateChannels = computed({
    get() {
        return store.state.adstatisticsContent.channelDates.jixFormat.start;
    },
    set(val) {
        store.commit('updateStartDateChannels', val);
    }
});

const endDateChannels = computed({
    get() {
        return store.state.adstatisticsContent.channelDates.jixFormat.end;
    },
    set(val) {
        store.commit('updateEndDateChannels', val);
    }
});

const startDateAreas = computed({
    get() {
        return store.state.adstatisticsContent.areaDates.jixFormat.start;
    },
    set(val) {
        store.commit('updateStartDateAreas', val);
    }
});

const endDateAreas = computed({
    get() {
        return store.state.adstatisticsContent.areaDates.jixFormat.end;
    },
    set(val) {
        store.commit('updateEndDateAreas', val);
    }
});

const startDateChannelsFormatted = computed(() => formatDate(new Date(adstatisticsContent.value.channelDates.isoFormat.start)));
const endDateChannelsFormatted = computed(() => formatDate(new Date(adstatisticsContent.value.channelDates.isoFormat.end)));
const startDateAreasFormatted = computed(() => formatDate(new Date(adstatisticsContent.value.areaDates.isoFormat.start)));
const endDateAreasFormatted = computed(() => formatDate(new Date(adstatisticsContent.value.areaDates.isoFormat.end)));

const statsYearly = computed(() => {
    return getStandardSplineChartConfig({
        chart: {
            type: 'areaspline',
            borderWidth: 0
        },
        title: {
            text: null
        },
        xAxis: {
            tickInterval: 4,
        },
        yAxis: {
            title: {
                text: T.noAds(),
            },
        },
        tooltip: {
            formatter: function() {
                const y_local = this.series.options.custom.getLocaleNumbers(this.y);
                let year = Math.floor(this.x);
                let quarter = Math.round(4 * (this.x - year)) + 1;
                return 'Q' + quarter + ' ' + year + ': ' + y_local + ' ' + T.ads();
            }
        },
        plotOptions: {
            series: {
                fillOpacity: 0.1,
                animation: false,
            },
        },
        series: [
            {
                name: T.previousQuartersLegend(),
                data: adstatisticsContent.value.previousQuarters,
                color: c1,
            },
            {
                name: T.currentQuartersLegend(),
                data: adstatisticsContent.value.currentQuarters,
                color: c2,
            },
        ],
    });
});

const statsChannels = computed(() => {
    return getStandardBarChartConfig({
        chart: {
            borderWidth: 0,
        },
        title: {
            text: null
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
        xAxis: {
            categories: adstatisticsContent.value.channels.map(channel => channel.name),
        },
        yAxis: {
            gridLineWidth: 0,
            min: 0,
        },
        tooltip: {
            formatter: function() {
                const y_local = this.series.options.custom.getLocaleNumbers(this.y);
                return this.x + ': ' + y_local;
            }
        },
        legend: {
            enabled: false
        },
        series: [
            {
                name: null,
                data: adstatisticsContent.value.channels.map(channel => channel.y),
                color: c1,
            },
        ],
    }, false);
});

const statsAreas = computed(() => {
    return getStandardBarChartConfig({
        chart: {
            borderWidth: 0,
        },
        title: {
            text: null
        },
        plotOptions: {
            series: {
                animation: false,
            },
        },
        xAxis: {
            categories: adstatisticsContent.value.areas.map(area => area.name),
        },
        yAxis: {
            gridLineWidth: 0,
            min: 0,
        },
        tooltip: {
            formatter: function() {
                const y_local = this.series.options.custom.getLocaleNumbers(this.y);
                return this.x + ': ' + y_local;
            }
        },
        legend: {
            enabled: false
        },
        series: [
            {
                name: null,
                data: adstatisticsContent.value.areas.map(area => area.y),
                color: c1,
            },
        ],
    }, false);
});

function showJobadDateModal(type) {
    if (type === 'channels') {
        jobaddatemodalchannels.value.show();
    } else if (type === 'areas') {
        jobaddatemodalareas.value.show();
    } else {
        throw new Error('Unknown type: ' + type);
    }
}

function onDatesUpdated() {
    store.dispatch('updateAdstatistics');
}
</script>

<template>
    <div>
        <h3> {{ T.header(companyName) }} </h3>
        <p class="mb-4">
            {{ T.intro(companyName) }}
        </p>
        <div class="bg-surface-default border p-2 p-md-4 mb-4">
            <h4> {{ T.yearlyHeader() }} </h4>
            <Highcharts :options="statsYearly" />
        </div>
        <div class="bg-surface-default border p-2 p-md-4 mb-4">
            <div class="d-flex justify-content-between">
                <h4> {{ T.channelHeader() }} </h4>
                <span>
                    <small class="mr-1"> {{ startDateChannelsFormatted }} - {{ endDateChannelsFormatted }} </small>
                    <DsIconButton
                        icon-name="button-edit"
                        type="secondary"
                        size="sm"
                        :label="T.editPeriod()"
                        @click="showJobadDateModal('channels')"
                    />
                </span>
            </div>
            <Highcharts :options="statsChannels" />
        </div>
        <div class="bg-surface-default border p-2 p-md-4">
            <div class="d-flex justify-content-between">
                <h4> {{ T.areaHeader() }} </h4>
                <span>
                    <small class="mr-1"> {{ startDateAreasFormatted }} - {{ endDateAreasFormatted }} </small>
                    <DsIconButton
                        icon-name="button-edit"
                        type="secondary"
                        size="sm"
                        :label="T.editPeriod()"
                        @click="showJobadDateModal('areas')"
                    />
                </span>
            </div>
            <Highcharts :options="statsAreas" />
        </div>
        <JobadDateModal
            ref="jobaddatemodalchannels"
            v-model:start-date="startDateChannels"
            v-model:end-date="endDateChannels"
            @dates-updated="onDatesUpdated"
        />
        <JobadDateModal
            ref="jobaddatemodalareas"
            v-model:start-date="startDateAreas"
            v-model:end-date="endDateAreas"
            @dates-updated="onDatesUpdated"
        />
    </div>
</template>
